

import React, { forwardRef, ReactNode, InputHTMLAttributes } from 'react';

// Omit the native size property which is a number
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  leftIcon?: ReactNode;
  leadingIcon?: ReactNode; // Alias for leftIcon
  startIcon?: ReactNode; // Alias for leftIcon (for EnhancedSearchBar.tsx compatibility)
  rightIcon?: ReactNode;
  endIcon?: ReactNode; // Alias for rightIcon (for EnhancedSearchBar.tsx compatibility)
  helpText?: string;
  fullWidth?: boolean;
  isLoading?: boolean;
  multiline?: boolean;
  rows?: number;
  size?: 'sm' | 'md' | 'lg'; // Our custom size prop
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      leftIcon,
      leadingIcon,
      startIcon,
      rightIcon,
      endIcon,
      helpText,
      fullWidth = false,
      isLoading = false,
      className = '',
      disabled,
      multiline = false,
      rows = 3,
      size = 'md',
      ...props
    },
    ref
  ) => {
    // Use leadingIcon or startIcon as alias for leftIcon if provided
    const effectiveLeftIcon = leadingIcon || startIcon || leftIcon;
    // Use endIcon as alias for rightIcon if provided
    const effectiveRightIcon = endIcon || rightIcon;
    // Base styles
    const baseInputStyle = 'appearance-none block rounded focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue transition-colors sm:text-sm';
    
    // Error or default border style
    const borderStyle = error
      ? 'border-error text-error placeholder-error-300'
      : 'border-border-medium dark:border-border-heavy placeholder-text-tertiary';
    
    // Width style
    const widthStyle = fullWidth ? 'w-full' : 'w-auto';

    // Size styles
    const sizeStyles = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base'
    };

    // Padding style based on icons and size
    let basePadding = 'px-3 py-2';
    if (size === 'sm') basePadding = 'px-2 py-1';
    if (size === 'lg') basePadding = 'px-4 py-3';

    let paddingStyle = basePadding;
    if (effectiveLeftIcon) paddingStyle = `pl-10 pr-3 ${basePadding.split(' ')[1]}`;
    if (effectiveRightIcon) paddingStyle = `pl-3 pr-10 ${basePadding.split(' ')[1]}`;
    if (effectiveLeftIcon && effectiveRightIcon) paddingStyle = `pl-10 pr-10 ${basePadding.split(' ')[1]}`;
    
    // Disabled style
    const disabledStyle = disabled || isLoading 
      ? 'bg-background-tertiary text-text-tertiary cursor-not-allowed' 
      : 'bg-background-primary text-text-primary';
    
    // Combine styles
    const combinedClassName = `${baseInputStyle} ${borderStyle} ${paddingStyle} ${widthStyle} ${disabledStyle} ${sizeStyles[size]} ${className}`;
    
    return (
      <div className={`${fullWidth ? 'w-full' : 'w-auto'}`}>
        {label && (
          <label htmlFor={props.id} className="block text-sm font-medium text-text-secondary dark:text-text-primary mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          {effectiveLeftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-text-tertiary sm:text-sm">{effectiveLeftIcon}</span>
            </div>
          )}
          
          {multiline ? (
            <textarea
              className={combinedClassName}
              disabled={disabled || isLoading}
              rows={rows}
              {...props as any}
            />
          ) : (
            <input
              ref={ref}
              className={combinedClassName}
              disabled={disabled || isLoading}
              {...props}
            />
          )}
          
          {effectiveRightIcon && !isLoading && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <span className="text-text-tertiary sm:text-sm">{effectiveRightIcon}</span>
            </div>
          )}
          
          {isLoading && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <svg className="animate-spin h-5 w-5 text-text-tertiary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
        </div>
        
        {(error || helpText) && (
          <p className={`mt-1 text-sm ${error ? 'text-error' : 'text-text-tertiary'}`}>
            {error || helpText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;